/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import {
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCookies } from "react-cookie";
// import * as CompanyEmailValidator from "company-email-validator";
import mixpanel from "mixpanel-browser";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// interfaces ---------------------------
interface AuthProviderProps {
  children: React.ReactNode;
}

export type BrandInfoCurrentSection =
  | "Welcome"
  | "Profile2"
  | "Contact"
  | "VerifyContact"
  | "Pricing";

interface AuthContextProps {
  handleOpenLinkedin: ({ backToSource }: { backToSource?: boolean }) => void;
  handleOpenGoogleLoginForUser: ({
    backToSource,
  }: {
    backToSource?: boolean;
  }) => void;
  getLinkedinDatatoLogin: (
    token: string
  ) => Promise<{ success: boolean; refresh: boolean }>;
  getGoogleDatatoLogin: () => Promise<{ success: boolean; refresh: boolean }>;
  getUserData: () => Promise<void>;
  getUserLkdProfileData: (slug: string) => Promise<void>;
  loggedUser: loggedUser | null;
  loggedUserLKdProfile: loggedUserLKdProfile | null;
  setReFetchUserData: React.Dispatch<SetStateAction<boolean>>;
  loggedBrand: loggedBrand;
  BrandSignIn;
  BrandSignUp;
  getBrandByEmail;
  getBrandData: () => Promise<void>;
  setLoggedBrand;
  setReFetchBrandData;
  sendingOTPFeature;
  verifyOTP;
  saveIntrestedCreator;
  getUserLocation: () => Promise<{
    region: string;
    ip: string;
    country: string;
    state: string;
    city: string;
    lon: string;
    lat: string;
    postalCode: string;
  }>;
}

interface loggedUser {
  name: string;
  email: string;
  activePlan: boolean;
  status: number;
  profile: string;
  gender: string;
  contributionLink: string;
  is_verified: boolean;
  slug: string;
  referralCode: string;
  earlyAccess?: boolean;
  mobile: string;
  linkedinLink: string;
  openForCollaboration: boolean;
  eaRank?: string;
  totalChips?: number;
  featuredData?: {
    value: boolean;
    topCreatorChips?: number;
  };
  buildProfileChips?: {
    AudienceInfo?: number | null;
    PersonalInfo?: number | null;
    CollabHistory?: number | null;
    MonetizationHistory?: number | null;
    ContentInfo?: number | null;
  };
  referralCount: number;
  refered_by: number;
  linkedinfollowers: string;
  extensionConsent: boolean;
  tagline: string;
  selectedWhatYouWillFind: number;
  profileStrengthStatus: number;
  extensionUsage: {
    usedCount: number;
    dailyLimit: number;
    lastSyncTime: string;
  };
}

interface loggedUserLKdProfile {
  avgLikes: number;
  avgComments: number;
  avgEngagement: number;
  avgEngagementPercent: any;
  avgReposts: number;
  followers: number;
  topThreeEngagedPosts: [];
  name: string;
  gender: string;
  tagline: string;
  location: string;
  profile_image: string;
  experience: string;
  badge: string;
  featured: [];
  profile_url: string;
  firstThirtyLikes: number[];
  firstThirtyComments: number[];
  topLinks: [];
}

interface loggedBrand {
  BrandId: string;
  email: string;
  isSampleBrand: boolean;
  PocName: string;
  PocContact: string;
  status: number;
  firstTime: boolean;
  isVerified: boolean;
  credits: number;
  activePlan: boolean;
  planName: string;
  planExpiry: Date;
  FixedAvailability: boolean;
  expireOn: Date;
  currentPlan: {
    planType: string;
    _id: string;
  };
  freeTierUsed: boolean;
  allBrandInfoFilled: boolean;
  currentProfileBuildingStep: BrandInfoCurrentSection;
  creditStats: {
    type: string;
    available: number;
    used: number;
    info: string;
  }[];
  addOn: {
    additionalCampaign: number;
    extraCollaboration: number;
    additionalTeamAccount: number;
  };
  latestCampaigns: {
    title: string;
    id: string;
    status: number;
  }[];
}

const AuthContext = createContext<AuthContextProps | null>(null);
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<AuthProviderProps> = (props) => {
  // route-1 . create influencer order ---------------------
  const [loggedUser, setLoggedUser] = useState<loggedUser | null>(null);
  const [loggedUserLKdProfile, setLoggedUserLKdProfile] =
    useState<loggedUserLKdProfile | null>(null);
  const [loggedBrand, setLoggedBrand] = useState(null);

  const [reFetchUserData, setReFetchUserData] = useState(false);
  const [reFetchBrandData, setReFetchBrandData] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies();
  const navigate = useNavigate();

  useEffect(() => {
    // Check UserType from localStorage and fetch data accordingly
    const userType = localStorage.getItem("UserType");
    if (userType === "Brand" && localStorage.getItem("jwtToken")) {
      getBrandData();
    } else if (localStorage.getItem("jwtToken")) {
      getUserData();
    }
  }, [reFetchUserData, reFetchBrandData]);

  useEffect(() => {
    if (loggedUser?.slug) {
      getUserLkdProfileData(loggedUser?.slug);
    }
  }, [loggedUser?.slug]);

  // open linkedin login page -----------------------
  const handleOpenLinkedin = async ({ backToSource = false }) => {
    try {
      if (backToSource) localStorage.setItem("back_to_source", "true");
      localStorage.setItem("signup_source", window.location.href); // Save the current URL in localStorage

      const response = await axios.get(`${host}/auth/linkedin`);

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in login with linkedin");
      } else {
        window.location.href = response.data.redirectURL;
      }
    } catch (error) {
      alert(error);
    }
  };

  // fetches the linkedin user data -------
  const getLinkedinDatatoLogin = async (token: string) => {
    const response = await axios.get(
      `${host}/auth/linkedin/success?token=${token}`
    );
    if (response.status === 200) {
      await LoginInfluncer(
        response.data?.picture,
        response.data?.name,
        response.data?.email
      );
    } else {
      return { success: false, refresh: true };
      throw new Error("Error in login with linkedin");
    }
  };

  const handleOpenGoogleLoginForUser = async ({
    backToSource = false,
  }: {
    backToSource?: boolean;
  }) => {
    try {
      if (backToSource) localStorage.setItem("back_to_source", "true");
      localStorage.setItem("signup_source", window.location.href); // Save the current URL in localStorage

      const response = await axios.get(`${host}/google/auth`);

      // Check if the response contains the LinkedIn authorization URL
      if (response.status !== 200) {
        throw new Error("Error in login with Google");
      } else {
        window.location.href = response.data.redirectURL;
      }
    } catch (error) {
      alert(error);
    }
  };

  // fetches the google user data -------
  const getGoogleDatatoLogin = async () => {
    try {
      const response = await axios.get(`${host}/google/login/success`, {
        withCredentials: true,
      });
      if (response.status === 200) {
        await LoginInfluncer(
          response.data?.res?.photo,
          response.data?.res?.name,
          response.data?.res?.email,
          response.data?.res?.id
        );
      } else {
        return { success: false, refresh: true };
        throw new Error("Error in login with Google");
      }
    } catch (error) {
      alert(error);
    }
  };

  // logoin the user and saves the token in the session
  const LoginInfluncer = async (
    profile: string,
    name: string,
    email: string,
    googleID?: string
  ) => {
    try {
      const location = await getUserLocation();

      const registeredFrom = localStorage.getItem("signup_source");

      const response = await axios.post(`${host}/influencer/login`, {
        profile,
        name,
        email,
        country: location?.country,
        state: location?.state,
        city: location?.city,
        registeredFrom,
        googleID,
      });

      if (response.status === 200) {
        if (response.data.success) {
          localStorage.setItem("jwtToken", response.data.token);
          localStorage.setItem("UserType", "Influencer");

          //Identify and track the user in Mixpanel
          mixpanel.identify(email);

          // if mixpanel has already props set then it would not be updated
          mixpanel.people.set_once({
            $first_name: name.split(" ")[0],
            $last_name: name.split(" ")[1],
            $email: email,
            isInfluencer: true,
          });

          // const redirectURL = localStorage.getItem("signup_source");
          toast.success("Successfully Logged in", { position: "top-center" });
          navigate("/influencer/dashboard");

          const redirectURL = localStorage.getItem("signup_source");
          const backToSource = localStorage.getItem("back_to_source");
          if (redirectURL?.includes(`/t/`)) {
            localStorage.setItem("Login_from_temp", "true");
          }
          if (redirectURL && backToSource) {
            window.open(redirectURL, "_self");
          }
        } else {
          throw new Error(response.data.error);
        }
      } else {
        throw new Error("Error in login with linkedin");
      }
    } catch (error) {
      alert(error);
      window.open("/", "_self");
    } finally {
      localStorage.removeItem("signup_source");
      localStorage.removeItem("back_to_source");
    }
  };

  const saveIntrestedCreator = async (email) => {
    try {
      const res = await axios.post(`${host}/influencer/saveIntrestedCreator`, {
        email,
      });
      return res.data;
    } catch (error) {
      alert(error);
    }
  };

  // fetches the user's data --------------
  const getUserData = async () => {
    try {
      const loc = await getUserLocation();
      let headers: {
        jwtToken: string;
        ["X-Loc"]?: string;
      } = {
        jwtToken: localStorage.getItem("jwtToken"),
      };
      // console.log(loc);
      if (loc && !loc?.saved) {
        headers = {
          ...headers,
          ["X-Loc"]: btoa(unescape(encodeURIComponent(JSON.stringify(loc)))),
        };
      }
      const response = await axios.get(`${host}/influencer/getInfluencerData`, {
        headers: headers,
      });

      if (response.status === 200) {
        if (response.data.success) {
          return setLoggedUser({
            ...response.data?.data,
            activePlan: response.data?.activePlan,
          });
        } else {
          if (response.data?.logout) {
            localStorage.removeItem("jwtToken");
            localStorage.removeItem("UserType");
            window.open("/", "_self");
            return;
          }
          throw new Error(response.data.error);
        }
      } else {
        window.open("/", "_self");
        throw new Error("Error in fetching user data");
      }
    } catch (error) {
      console.log(error);
      // alert("Error in fetching user data");
      // window.open("/", "_self");
    }
  };

  const getUserLkdProfileData = async (slug: string) => {
    try {
      const response = await axios.get(
        `${host}/influencer/linkedin/temporary/profile?slug=${slug}`
      );
      // Check if the response contains the LinkedIn authorization URL
      if (response.status == 200) {
        return setLoggedUserLKdProfile(response.data?.LinkedinAnalysis);
      } else {
        throw new Error(response?.statusText);
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  //Brand
  const getBrandByEmail = async (email) => {
    try {
      const response = await axios.get(`${host}/Brand/getBrandByEmail`, {
        params: { email }, // Pass email as a parameter
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return { Error: response.data.error };
      }
    } catch (e) {
      return { Error: e.response.data.error };
    }
  };

  const sendingOTPFeature = async (email) => {
    // var emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    // const emailPattern =
    //   /\b[A-Za-z0-9._%+-]+@(?:gmail\.com|protonmail\.com|zoho\.com|outlook\.com|yahoo\.com|aol\.com|gmx\.com|icloud\.com|mailfence\.com|tutanota\.com|neo\.com|hubspot\.com|thunderbird\.net|titan\.com|yandex\.com|hotmail\.com|live\.com|msn\.com|fastmail\.com|mail\.com|posteo\.de|t-online\.de|bluemail\.me|hushmail\.com|rediffmail\.com|rediff\.com|redmail\.com|mailinator\.com)\b/;

    // if (
    //   !emailPattern.test(email) ||
    //   CompanyEmailValidator.isCompanyEmail(email)
    // ) {
    const response = await fetch(`${host}/email/sendOTPViaEmail`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Access-Control-Allow-Credentials": "true",
      },
      body: JSON.stringify({
        email,
      }),
    });
    const json = await response.json();
    if (json?.success) {
      const otpcode = parseInt(String(json.code - 5626)) * 562002;
      setCookie("ccoondfe", otpcode, { maxAge: 120 }); // valid for two minute
      return json;
    } else {
      return { Error: "Oops! Something went Wrong!" };
    }
    // } else {
    //   return { Error: "Invalid Email! Please Enter Company Mail" };
    // }
  };

  const verifyOTP = async (otp, email, type) => {
    if (otp?.length !== 4) {
      return { Error: "Please Enter a Proper Code" };
    } else {
      const code = cookies?.ccoondfe;
      if (!code) {
        return { Error: "OTP was valid for 2 minute, Please retry again" };
      } else {
        if (parseInt(otp) === parseInt(String(parseInt(code) / 562002))) {
          // alert("Verification was successfull");
          removeCookie("ccoondfe");
          if (type === "SignUp") {
            return await BrandSignUp(email);
          } else if (type === "Login") {
            return await BrandSignIn(email);
          }
        } else {
          return { Error: "Invalid Otp" };
        }
      }
    }
  };

  const BrandSignUp = async (email) => {
    try {
      const emailDomain = email?.split("@")[1].toLowerCase();
      const forbiddenDomains = [
        "gmail.com",
        "yahoo.com",
        "outlook.com",
        "hotmail.com",
        "aol.com",
        "icloud.com",
        "protonmail.com",
        "mail.com",
        "zoho.com",
        "yandex.com",
        "gmx.com",
        "live.com",
        "msn.com",
        "inbox.com",
        "mail.ru",
      ];
      if (forbiddenDomains.includes(emailDomain)) {
        return {
          Error:
            "Please use your company email ID. Personal email IDs are not accepted.",
        };
      }
      const response = await axios.post(`${host}/Brand/SignUp`, { email });
      if (response.status === 200) {
        if (response.data.success) {
          setLoggedBrand(response.data.email);
          return await BrandSignIn(email);
        } else {
          // alert(response.data.error);
          return { Error: response.data.error };
        }
      } else {
        // alert(response.data.error);
        return { Error: response.data.error };
      }
    } catch (e) {
      console.log(e.response.data.error);
      return { Error: e.response.data.error };
    }
  };

  const BrandSignIn = async (email) => {
    try {
      const response = await axios.post(`${host}/Brand/SignIn`, {
        email,
      });

      if (response.status === 200) {
        if (response.data.success) {
          localStorage.setItem("jwtToken", response.data.token);
          localStorage.setItem("UserType", "Brand");

          // Identify and track the user in Mixpanel
          mixpanel.identify(email);
          mixpanel.people.set_once({
            $email: email,
            isBrand: true,
          });

          window.open("/Brand/DashBoard", "_self");
        } else {
          return { Error: response.data.error };
        }
      } else {
        return { Error: response.data.error };
      }
    } catch (error) {
      return { Error: error.response.data.error };
      // window.open("/", "_self");
    }
  };
  const getBrandData = async () => {
    try {
      const response = await axios.get(`${host}/Brand/getBrandData`, {
        headers: {
          jwtToken: localStorage.getItem("jwtToken"),
        },
      });

      // This will only run for 2xx status codes
      if (response.data.success) {
        return setLoggedBrand({
          ...response.data?.data,
        });
      } else {
        // Handle unsuccessful response with 2xx status
        if (response.data?.logout) {
          localStorage.removeItem("jwtToken");
          localStorage.removeItem("UserType");
          window.open("/", "_self");
        }
        throw new Error(response.data.error);
      }
    } catch (error) {
      console.log(error);

      // Check if it's an Axios error with a response
      if (error.response) {
        // Check for logout flag in the response data
        if (error.response.data?.logout) {
          console.log("Logging Out");
          localStorage.removeItem("jwtToken");
          localStorage.removeItem("UserType");
          window.open("/", "_self");
          return;
        }
      }

      // You can handle other error cases or display a generic error
      // alert("Error in fetching Brand data");
    }
  };

  // get current user location -----------------------
  const getUserLocation = async () => {
    let location: any =
      JSON.parse(sessionStorage.getItem("location")) || undefined;
    if (location?.country) {
      return {
        ...location,
        saved: true,
      };
    }

    try {
      // console.log("Getting user location");
      const getIPRes = await axios.get(`https://httpbin.org/ip`);
      if (getIPRes.status === 200) {
        const ip = getIPRes?.data?.origin;
        // console.log(ip);
        sessionStorage.setItem("ip", ip);

        try {
          const getLocation = await axios.get(`https://ipinfo.io/${ip}/json`);
          if (getLocation.status === 200) {
            // console.log(getLocation?.data);
            location = {
              ip: ip,
              country: getLocation?.data?.country,
              state: getLocation?.data?.region,
              city: getLocation?.data?.city,
              lon: getLocation?.data?.loc?.split(",")?.[0],
              lat: getLocation?.data?.loc?.split(",")?.[1],
              postalCode: getLocation?.data?.postal,
            };
            sessionStorage.setItem("location", JSON.stringify(location));
          } else {
            throw new Error("Failed to get location from ipapi.co");
          }
        } catch (error) {
          // console.log("Error with ipapi.co, trying ipinfo.io:", error);
          const getLocation = await axios.get(`https://ipinfo.io/${ip}/json`);
          if (getLocation.status === 200) {
            // console.log(getLocation?.data);
            location = {
              ip: ip,
              country: getLocation?.data?.country,
              state: getLocation?.data?.region,
              city: getLocation?.data?.city,
              lon: getLocation?.data?.loc?.split(",")?.[0],
              lat: getLocation?.data?.loc?.split(",")?.[1],
              postalCode: getLocation?.data?.postal,
            };
            sessionStorage.setItem("location", JSON.stringify(location));
          } else {
            throw new Error("Failed to get location from ipinfo.io");
          }
        }
      } else {
        throw new Error("Failed to get IP from httpbin.org");
      }
    } catch (error) {
      console.log("Error getting user location:", error);
    }

    return location;
  };

  return (
    <AuthContext.Provider
      value={{
        handleOpenLinkedin,
        handleOpenGoogleLoginForUser,
        getLinkedinDatatoLogin,
        getGoogleDatatoLogin,
        getUserData,
        loggedUser,
        getUserLkdProfileData,
        loggedUserLKdProfile,
        setReFetchUserData,
        loggedBrand,
        BrandSignIn,
        getBrandByEmail,
        BrandSignUp,
        getBrandData,
        setReFetchBrandData,
        sendingOTPFeature,
        setLoggedBrand,
        verifyOTP,
        saveIntrestedCreator,
        getUserLocation,
      }}
    >
      {props?.children}
    </AuthContext.Provider>
  );
};
